import { getRequestError } from '../../shared/requestError';
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  DefaultContext,
  DocumentNode,
  gql,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { SetContextLink } from '@apollo/client/link/context';
import { LocalState } from '@apollo/client/local-state';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { catchError, from as rxFrom, map, mergeMap, throwError } from 'rxjs';
import { LocalStorageKeys } from '../../enums/EnumsValues';
import { ApolloContextKey } from '../../enums/ApolloContextKey';
import { BackendError, isBackendError } from '../../shared/BackendError';
import { ErrorLink } from '@apollo/client/link/error';
import { Mutation } from '../../services/graphql/mutation';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { Storage } from '@ionic/storage';
import { RetryLink } from '@apollo/client/link/retry';
import { v4 as uuidv4 } from 'uuid';
import {
  optimisticValuesToReplaceByOperation,
  queryRequireTenantId,
  rollbackCacheHandlerByName,
} from '../../services/graphql/cache';
import { stringify, parse } from 'flatted';
import { InternalError, Tools } from '../../shared/Tools';
import { Query } from '../../services/graphql/query';
import CustomSerializingLink from '../../links/CustomSerializingLink';
import QueueLink from '../../links/QueueLink';
import { restoreAndPersistApolloCache } from './persistApolloCache';
import { PageLoading } from '@ant-design/pro-layout';
import { GRAPHQL_RECONNECTED } from '../../shared/graphqlConnection';
import { TrackedMutationCoordinator } from './TrackedMutationCoordinator';
import i18n from '../../i18n/i18n';

export interface TrackedQuery {
  ownerId?: string;
  contextJSON: string;
  id: string;
  name: string;
  queryJSON: string;
  variablesJSON: string;
}

const checkMaintenanceModeAndRedirect = () => {
  if (window.location.pathname !== '/maintenance') {
    window.location.href = '/maintenance';
  }
};

const isLoginOperation = (operation: ApolloLink.Operation) => {
  const context = operation.getContext();
  const operationName =
    context[ApolloContextKey.CUSTOM_OPERATION_NAME] || operation.operationName;
  return operationName === 'login' || operationName === 'loginFirebase';
};

const backendUri =
  import.meta.env.VITE_BACKEND_WS || 'http://localhost:3003/graphql';

const wsUri =
  import.meta.env.VITE_BACKEND_WEBSOCKET || 'ws://localhost:3003/graphql';

/**
 * Link que permite cerrar el flujo de mutaciones y encolarlas, para ser lanzadas cuando se desee
 */
const queueLink = new QueueLink();

/**
 * Link custom que permite serializar las mutaciones según las keys que el programador haya indicado en cada mutación
 */
const customSerializingLink = new CustomSerializingLink();

/**
 * Link que se encarga de reintentar las mutaciones ante un error, según se le indique
 * En este caso solo las reintenta cuando se tiene un error de conexión
 * Los reintentos se dan en un intervalo que va incrementando tras cada intento.
 * A su vez esta randomizado el intervalo de forma que no se envien demasiadas consultas al backend al mismo tiempo
 */
const retryLink = new RetryLink({
  attempts: {
    // Cantidad máxima de reintentos

    max: 5, // default
    // max: Infinity,

    retryIf(error) {
      return !Tools.isGraphqlError(error);
    },
  },
});

/**
 * La propiedad "isOptimistic" solo esta presente para el usuario, y siempre debe contener un valor booleano. Por defecto es false
 */
const isOptimisticTypePolicy = {
  fields: {
    isOptimistic: {
      read(isOptimistic = false) {
        return isOptimistic;
      },
    },
  },
};

const cache = new InMemoryCache({
  typePolicies: {
    UserRole: {
      keyFields: ['role_id', 'user_id'], // add normalization because this type does not have an "id" field
    },
    Query: {
      fields: {
        /**
         * Permite que al consultar una compania particular, en caso de no tenerla cacheada, se consulta
         * si se encuentra cacheada en la consulta de listado de companias
         */
        company: {
          read(_, { args, toReference }) {
            return toReference({
              __typename: 'Company',
              id: args?.id,
            });
          },
        },
        // Invitations are fetched as complete, filtered lists; each response
        // should replace the previous result, including when it is empty.
        invitations: {
          merge: false,
        },
      },
    },
    Operation: isOptimisticTypePolicy,
    Company: isOptimisticTypePolicy,
  },
});

const terminatingLink = new UploadHttpLink({
  credentials: 'include',
  uri: backendUri,
});

let hasConnectedWebSocket = false;
const subscriptionClient = createClient({
  url: wsUri,
  retryAttempts: Infinity,
  on: {
    connected: () => {
      if (hasConnectedWebSocket) {
        window.dispatchEvent(new Event(GRAPHQL_RECONNECTED));
      } else {
        hasConnectedWebSocket = true;
        console.info('GraphQL WebSocket connected');
      }
    },
    error: (error) => {
      console.error('GraphQL WebSocket connection failed:', error);
    },
  },
});
const wsLink = new GraphQLWsLink(subscriptionClient);

const setOperationAuthorization = (
  operation: ApolloLink.Operation,
  token: string | null,
) => {
  const authorization = `Bearer ${token}`;
  operation.extensions = {
    ...operation.extensions,
    authorization,
  };
  operation.setContext((context: DefaultContext) => ({
    headers: {
      ...context.headers,
      Authorization: authorization,
    },
  }));
};

const handleCustomOperationResponse = (
  response: unknown,
  operation: ApolloLink.Operation,
): void => {
  if (!response || typeof response !== 'object') {
    throw new BackendError(null, i18n.t('error.invalidResultQuery'));
  }
  const result = response as Record<string, unknown>;
  const typename = result.__typename;

  if (!typename) {
    throw new BackendError(null, i18n.t('error.invalidResultQuery'));
  }
  if (typename !== 'ResultError') return;

  const statusCode =
    typeof result.status_code === 'string' ||
    typeof result.status_code === 'number'
      ? result.status_code
      : null;
  const message =
    typeof result.message === 'string' ? result.message : 'Error general';
  const translationKey =
    typeof result.message_translation_key === 'string'
      ? result.message_translation_key
      : undefined;
  if (statusCode === 9009 && !isLoginOperation(operation)) {
    checkMaintenanceModeAndRedirect();
  }
  throw new BackendError(statusCode, message, translationKey);
};

const handleCustomOperationError = (
  data: Record<string, unknown>,
  operationName: unknown,
  operation: ApolloLink.Operation,
): void => {
  if (typeof operationName !== 'string') return;
  const response = data[operationName];
  if (!response || (Array.isArray(response) && response.length === 0)) return;
  handleCustomOperationResponse(
    Array.isArray(response) ? response[0] : response,
    operation,
  );
};

const addTokenLink = new ApolloLink((operation, forward) => {
  const localToken = Tools.getStorage(LocalStorageKeys.Token);
  setOperationAuthorization(operation, localToken);
  return forward(operation);
});

const interceptResponseForResultErrorLink = new ApolloLink(
  (operation, forward) => {
    return forward(operation).pipe(
      map((result) => {
        const customOperationName =
          operation.getContext()[ApolloContextKey.CUSTOM_OPERATION_NAME];
        if (result.data && customOperationName) {
          handleCustomOperationError(
            result.data,
            customOperationName,
            operation,
          );
        }
        if (result.errors?.[0] && isBackendError(result.errors[0])) {
          throw new BackendError(
            result.errors[0].status_code,
            result.errors[0].message,
            result.errors[0].message_translation_key,
          );
        }
        return result;
      }),
    );
  },
);

const refreshToken = async (): Promise<string> => {
  const accessToken = Tools.getStorage(LocalStorageKeys.Token);
  if (!accessToken) throw new Error('Access token not found');
  const requestHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  const refreshTokenResponse = await fetch(backendUri, {
    method: 'POST',
    body: JSON.stringify({
      operationName: Mutation.refreshToken.name,
      query: Mutation.refreshToken.gql,
    }),
    headers: requestHeaders,
    credentials: 'include',
    cache: 'no-cache',
  });

  if (!refreshTokenResponse.ok) throw new Error('Could not refresh token');

  const response = await refreshTokenResponse.json();

  const newAccessToken = response.data?.refreshToken?.accessToken;

  if (!newAccessToken) throw new Error('Refresh token not found in response');

  Tools.setStorage(LocalStorageKeys.Token, newAccessToken);

  return newAccessToken;
};

const refreshTokenLink = new ErrorLink(
  ({ operation, forward, error, result }) => {
    const responseErrors =
      result?.errors ?? (CombinedGraphQLErrors.is(error) ? error.errors : []);
    const hasInvalidTokenError = responseErrors.some(
      (item) => isBackendError(item) && item.status_code === 401,
    );
    if (!hasInvalidTokenError) {
      return;
    }
    return rxFrom(refreshToken()).pipe(
      mergeMap((newToken) => {
        setOperationAuthorization(operation, newToken);
        return forward(operation);
      }),
      catchError((err: unknown) => {
        console.error(err);
        const expiredSessionEvent = new Event('sessionExpired');
        window.dispatchEvent(expiredSessionEvent);
        return throwError(() => err);
      }),
    );
  },
);

const isSubscription = ({ query }: { query: DocumentNode }) => {
  const definition = getMainDefinition(query);
  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'subscription'
  );
};

const getOptimisticResponseCacheId = (operation: ApolloLink.Operation) => {
  const context = operation.getContext();
  const optimisticResponse = Tools.getOptimisticResponse(context);
  if (!optimisticResponse || !operation.operationName) return;
  const entity = optimisticResponse[operation.operationName];
  if (!entity) return;
  const id = `${entity.__typename}:${entity.id}`;
  return id;
};

type RollbackHandler = (
  queryData: unknown,
  mutationData?: unknown,
  previousData?: unknown,
) => unknown;

const rollbackTrackedCache = (
  operation: ApolloLink.Operation,
  context: DefaultContext,
): void => {
  const rollbackQueries =
    rollbackCacheHandlerByName[
      operation.operationName as keyof typeof rollbackCacheHandlerByName
    ] || {};

  for (const [query, rollback] of Object.entries(rollbackQueries) as [
    string,
    RollbackHandler,
  ][]) {
    if (!(query in Query)) continue;

    const tenantId = queryRequireTenantId.includes(query)
      ? operation.variables?.tenant_id || operation.variables?.input?.tenant_id
      : undefined;
    operation.client.cache.updateQuery<unknown>(
      {
        query: gql(Query[query as keyof typeof Query].gql),
        variables: tenantId ? { tenant_id: tenantId } : undefined,
      },
      (data) => {
        const optimisticReplacements = Tools.getStorage(
          LocalStorageKeys.optimisticValuesToReplace,
        );
        const previousValue = context.previousValue
          ? structuredClone(context.previousValue)
          : undefined;
        if (previousValue) {
          optimisticReplacements?.forEach(([optimisticValue, realValue]) => {
            Tools.replaceValuesInObj(previousValue, optimisticValue, realValue);
          });
        }
        const optimisticResponse = Tools.getOptimisticResponse(context);
        const operationName = operation.operationName;
        return rollback(
          data,
          operationName ? optimisticResponse?.[operationName] : undefined,
          previousValue,
        );
      },
    );
  }
};

// One owner per document, including provider remounts. Presence lasts until the tab closes.
const mutationCoordinator = new TrackedMutationCoordinator(
  navigator.locks,
  uuidv4(),
);

const ApolloProviderWrapper = ({ children }: { children: ReactNode }) => {
  const [online, setOnline] = useState(navigator.onLine);
  const [cachePersisted, setCachePersisted] = useState(false);
  const [client, setClient] = useState<ApolloClient>();
  const [trackedLoaded, setTrackedLoaded] = useState(false);
  const executeRef = useRef<
    Promise<PromiseSettledResult<unknown>[] | undefined> | undefined
  >(undefined);
  const clientRef = useRef<ApolloClient | undefined>(undefined);
  const clientInitializationRef = useRef<Promise<ApolloClient> | null>(null);

  const getTrackedQueries = (): TrackedQuery[] => {
    return Tools.getStorage(LocalStorageKeys.TrackedQueries) || [];
  };

  function trackedQueriesAdd(trackedQuery: TrackedQuery) {
    Tools.setStorage(LocalStorageKeys.TrackedQueries, [
      ...getTrackedQueries(),
      trackedQuery,
    ]);
  }

  function trackedQueriesRemove(id: string) {
    Tools.setStorage(
      LocalStorageKeys.TrackedQueries,
      getTrackedQueries().filter((currQuery) => currQuery.id !== id),
    );
  }

  const executingMutations = useRef<string[]>([]).current;
  const errorLink = new ErrorLink(({ operation, error: linkError, result }) => {
    const graphQLErrors = CombinedGraphQLErrors.is(linkError)
      ? linkError.errors
      : (result?.errors ?? []);
    const maintenanceModeError = graphQLErrors.some((error) => {
      const statusCode = isBackendError(error)
        ? error.status_code
        : error.extensions?.status_code;
      return Number(statusCode) === 9009;
    });
    if (maintenanceModeError && !isLoginOperation(operation)) {
      checkMaintenanceModeAndRedirect();
    }

    const context = operation.getContext();
    if (!context.tracked) return;

    const executingIndex = executingMutations.indexOf(context.trackedQueryId);
    if (executingIndex >= 0) executingMutations.splice(executingIndex, 1);

    const shouldRollback =
      CombinedGraphQLErrors.is(linkError) ||
      Boolean(result?.errors?.length) ||
      Tools.isGraphqlError(linkError) ||
      linkError instanceof InternalError;
    if (shouldRollback) {
      rollbackTrackedCache(operation, context);
      trackedQueriesRemove(context.trackedQueryId);
    }
  });

  const updateRealValuesLink = new ApolloLink((operation, forward) => {
    const context = operation.getContext();
    const name = operation.operationName ?? '';

    if (!(context.tracked && name in optimisticValuesToReplaceByOperation)) {
      return forward(operation);
    }

    const optimisticData = { ...Tools.getOptimisticResponse(context) };

    const currentOptimisticValuesToReplaceArray = Tools.getStorage(
      LocalStorageKeys.optimisticValuesToReplace,
    );
    currentOptimisticValuesToReplaceArray?.forEach(
      ([optimisticValue, realValue]) => {
        Tools.replaceValuesInObj(
          operation.variables,
          optimisticValue,
          realValue,
        );
      },
    );

    return forward(operation).pipe(
      map((data) => {
        const getOptimisticValuesToReplace =
          optimisticValuesToReplaceByOperation[
            name as keyof typeof optimisticValuesToReplaceByOperation
          ];
        const replaceOptimisticValues = getOptimisticValuesToReplace as
          | ((
              optimisticResponse: Record<string, unknown>,
              result: ApolloLink.Result<Record<string, unknown>>,
            ) => ([string, string] | [number, number])[])
          | undefined;
        const optimisticValuesToReplace =
          replaceOptimisticValues?.(
            optimisticData,
            data as ApolloLink.Result<Record<string, unknown>>,
          ) ?? [];
        const currentOptimisticValuesToReplaceArray =
          Tools.getStorage(LocalStorageKeys.optimisticValuesToReplace) || [];
        Tools.setStorage(LocalStorageKeys.optimisticValuesToReplace, [
          ...currentOptimisticValuesToReplaceArray,
          ...optimisticValuesToReplace,
        ]);

        return data;
      }),
    );
  });

  // TRACKED QUERIES
  const execute = async (mutations: (string | undefined)[] = []) => {
    if (executeRef.current) return executeRef.current;
    if (!online) {
      setTrackedLoaded(true);
      return;
    }
    executeRef.current = mutationCoordinator.replay(
      getTrackedQueries,
      (item) =>
        mutations.includes(item.id) || executingMutations.includes(item.id),
      async (trackedQuery) => {
        const context = parse(trackedQuery.contextJSON);
        try {
          return await clientRef.current!.mutate({
            context: {
              ...context,
              rerun: true,
              _optimisticResponse: context.optimisticResponse,
              optimisticResponse: undefined,
            },
            mutation: gql(trackedQuery.queryJSON) as TypedDocumentNode<
              Record<string, unknown>,
              OperationVariables
            >,
            update: Tools.updateCache,
            variables: parse(trackedQuery.variablesJSON),
          });
        } catch (error: unknown) {
          Tools.processQueryError(error);
          throw Object.assign(getRequestError(error), {
            queryContext: context,
          });
        }
      },
    );
    try {
      return await executeRef.current;
    } finally {
      executeRef.current = undefined;
      setTrackedLoaded(true);
    }
  };

  const persistOptimisticDataLink = new ApolloLink((operation, forward) => {
    // Apollo 4 exposes the owning cache through operation.client, not context.
    const context = operation.getContext();
    if (
      context.tracked &&
      Tools.getOptimisticResponse(context) &&
      context.rerun !== true
    ) {
      // Persist optimistic response

      // Store data in cache ROOT LAYER
      operation.client.cache.writeQuery({
        query: operation.query,
        data: Tools.getOptimisticResponse(context),
      });
      // Run cache updates for this mutation
      Tools.updateCache(
        operation.client.cache,
        { data: Tools.getOptimisticResponse(context) },
        { context, variables: operation.variables },
      );
    }

    return forward(operation);
  });

  const trackerLink = new ApolloLink((operation, forward) => {
    const context = operation.getContext();

    // If query can't be offline
    if (!context.tracked) {
      return forward(operation);
    }

    // Else if query can be offline
    let id = context.trackedQueryId;

    if (!id) {
      // If query wasn't stored
      id = uuidv4();

      operation.setContext((currContext: DefaultContext) => ({
        ...currContext,
        trackedQueryId: id,
      }));
      const newContext = operation.getContext();
      const name = operation.operationName ?? '';
      const queryJSON: string = operation.query.loc?.source.body || '';
      const variablesJSON: string = stringify(operation.variables);
      const contextJSON = stringify({ ...newContext, cache: undefined });

      // Store query
      trackedQueriesAdd({
        ownerId: mutationCoordinator.ownerId,
        contextJSON,
        id,
        name,
        queryJSON,
        variablesJSON,
      });
    }

    const exists = executingMutations.includes(id);
    if (!exists) {
      executingMutations.push(id);
    }
    return forward(operation).pipe(
      map((data) => {
        executingMutations.splice(executingMutations.indexOf(id), 1);
        // Remove query from store
        const cache = operation.client.cache;
        const optimisticResponseCacheId =
          getOptimisticResponseCacheId(operation);
        if (optimisticResponseCacheId) {
          cache.evict({ id: optimisticResponseCacheId });
        }
        cache.gc();
        trackedQueriesRemove(id);
        return data;
      }),
    );
  });

  const waitExecutedLink = new SetContextLink(async (context) => {
    let connectionError;

    if (!context.rerun) {
      const result = await execute([
        ...executingMutations,
        context.trackedQueryId,
      ]);
      const errorRelated = result?.find(
        (res) =>
          res.status === 'rejected' &&
          Tools.isGraphqlError(getRequestError(res.reason)) &&
          context.serializationKey?.length &&
          context.serializationKey.some((key: number) =>
            res.reason.queryContext.serializationKey.includes(key),
          ),
      ) as PromiseRejectedResult | undefined;
      if (errorRelated) {
        throw new InternalError(
          `Falló la consulta relacionada ${
            errorRelated.reason.queryContext[
              ApolloContextKey.CUSTOM_OPERATION_NAME
            ]
          }`,
        );
      }

      const errorDueToConnection = result?.find(
        (res) =>
          res.status === 'rejected' &&
          !Tools.isGraphqlError(getRequestError(res.reason)) &&
          !(getRequestError(res.reason) instanceof InternalError),
      ) as PromiseRejectedResult | undefined;
      if (errorDueToConnection) {
        connectionError = new Error(errorDueToConnection.reason.message);
      }
    }
    return { ...context, connectionError };
  });

  const throwRelatedErrorLink = new ApolloLink((operation, forward) => {
    const context = operation.getContext();
    if (context.connectionError) {
      throw context.connectionError;
    }
    return forward(operation);
  });

  const queryIgnoreQueueLink = new SetContextLink((context, operation) => {
    const definition = getMainDefinition(operation.query);
    const isQuery =
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'query';
    return {
      ...context,
      skipQueue: isQuery,
    };
  });

  useEffect(() => {
    const initClient = async () => {
      await mutationCoordinator.ready;
      const store = new Storage();
      await store.create();

      await restoreAndPersistApolloCache(cache, store);

      const clientInstance = new ApolloClient({
        cache,
        localState: new LocalState(),
        link: ApolloLink.from([
          errorLink,
          // Request links
          persistOptimisticDataLink,
          trackerLink,
          waitExecutedLink,
          throwRelatedErrorLink, // (throws if related error in waitExecutedLink)
          queryIgnoreQueueLink,
          queueLink,
          customSerializingLink,
          updateRealValuesLink,
          retryLink,
          addTokenLink,
          // Response links
          interceptResponseForResultErrorLink,
          refreshTokenLink,
          // Terminating link
          ApolloLink.split(
            isSubscription,
            wsLink,
            terminatingLink as unknown as ApolloLink,
          ),
        ]),
        defaultOptions: {
          query: {
            fetchPolicy: 'cache-first',
          },
          watchQuery: {
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true,
          },
          mutate: {
            fetchPolicy: 'network-only',
          },
        },
      });

      clientRef.current = clientInstance;

      void execute().catch(console.error);

      return clientInstance;
    };

    let active = true;
    clientInitializationRef.current ??= initClient();
    void clientInitializationRef.current
      .then((instance) => {
        if (active) {
          setCachePersisted(true);
          setClient(instance);
        }
      })
      .catch(console.error);
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    function onlineHandler() {
      setOnline(true);
    }

    function offlineHandler() {
      setOnline(false);
    }

    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);

    return () => {
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
    };
  }, []);

  useEffect(() => {
    if (online) {
      queueLink.open();
      if (clientRef.current) void execute().catch(console.error);
    } else {
      queueLink.close();
    }
  }, [online]);

  if (!client) {
    return <PageLoading />;
  }

  if (!cachePersisted) {
    return <PageLoading />; // Loading Apollo Client Persistence
  }
  if (!trackedLoaded) {
    return <PageLoading />; // Loading Tracked Queries
  }
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloProviderWrapper;
